/*
 * Authors: Koen, mariushirjanu
 * Created: 04/09/2020
 * Upgraded: 24/03/2023
 */
import { Injectable } from '@angular/core';
import { ILocationExtended } from '../../../../../type_definitions/datatypes';
import { FormattingService } from '../../../../shared/services/formatting.service';
import { GaFormlyService, IExtraFieldConfigurationObject, OnChangeHandler } from '../../../../shared/formly/ga-formly.service';
import { TranslationService } from '../../../../shared/translation/translation.service';
import { IAddressData } from './base-order-entry-location-to.service';

const labelClass:string = 'col-sm-3';
const valueClass:string = 'col-sm-8';

@Injectable()
export class OrderEntryLocationFormService {
  constructor(
    private formlyService:GaFormlyService,
    private formattingService:FormattingService,
    private translationService:TranslationService
  ) {
    /***/
  }

  public createCountryField(changeEventHandler?:OnChangeHandler, key?:string, label?:string):IExtraFieldConfigurationObject {
    return this.formlyService.aSelect(key || 'country', label || 'GA.GENERAL.COUNTRY')
      .isRequired()
      .withFormat(this.formattingService.formatCountry)
      .withLabelClass(labelClass)
      .withValueClass(valueClass)
      .withDisabled(false)
      .onChange(changeEventHandler)
      .build();

  }

  public createStateField(changeEventHandler?:OnChangeHandler, key?:string, label?:string):IExtraFieldConfigurationObject {
    return this.formlyService.aSelect(key || 'state', label || 'GA.GENERAL.STATE')
      .isRequired()
      .isHide()
      .withFormat(this.formattingService.formatState)
      .withLabelClass(labelClass)
      .withValueClass(valueClass)
      .withDisabled(false)
      .onChange(changeEventHandler)
      .build();
  }

  public createLocationField(changeEventHandler:OnChangeHandler, key?:string, label?:string):IExtraFieldConfigurationObject {
    return this.formlyService.aSelect2(key || 'location', label || 'GA.GENERAL.LOCATION')
      .isRequired()
      .withLabelClass(labelClass)
      .withValueClass(valueClass)
      .withDisabled(false)
      .withFormat(this.formattingService.formatLocationHtml)
      .withExtraOptions({
        templateOptions: {
          parseHtml: true,
          noTrack: true
        }
      })
      .onChange(changeEventHandler)
      .build();
  }

  public createOfficeField(key?:string, label?:string):IExtraFieldConfigurationObject {
    return this.formlyService.anInput(key || 'office', label || 'GA.GENERAL.OFFICE')
      .withMaxLength(200)
      .withLabelClass(labelClass)
      .withValueClass(valueClass)
      .build();
  }

  public createDefaultAddressField(key?:string, label?:string):IExtraFieldConfigurationObject {
    return this.formlyService.aCheckbox(key || 'defaultAddress', label || 'ORDERENTRY.SHIPPING.ADDRESS.DEFAULT')
      .withoutWrapper()
      .withWrapperClass('row col-md-6 align-self-center col-space-bottom')
      .build();
  }

  public createCompanyField(key?:string, label?:string):IExtraFieldConfigurationObject {
    return this.formlyService.anInput(key || 'company', label || 'GA.GENERAL.COMPANY')
      .withMaxLength(200)
      .isRequired()
      .withLabelClass(labelClass)
      .withValueClass(valueClass)
      .build();
  }

  public createTaxField(key?:string, label?:string):IExtraFieldConfigurationObject {
    return this.formlyService.anInput(key || 'tax', label || 'GA.GENERAL.VATTAX')
      .withMaxLength(30)
      .withLabelClass(labelClass)
      .withValueClass(valueClass)
      .withPattern(/.*/)
      .build();
  }

  public createAddressGroupField():IExtraFieldConfigurationObject {
    return this.formlyService.aGroup(undefined, 'GA.GENERAL.ADDRESS')
      .withLabelClass(labelClass)
      .withValueClass(valueClass)
      .withFields([
        this.formlyService.anInput('address', 'GA.GENERAL.ADDRESS')
          .withPlaceholder('GA.GENERAL.ADDRESS')
          .withMaxLength(300)
          .isRequired()
          .withoutWrapper()
          .withWrapperClass('col-sm-12 col-space-bottom')
          .build(),
        this.formlyService.anInput('zip', 'GA.GENERAL.ZIP')
          .withPlaceholder('GA.GENERAL.ZIP')
          .withMaxLength(20)
          .isRequired()
          .withoutWrapper()
          .withWrapperClass('col-4 col-xsm-5 col-sm-4 col-hug-right')
          .build(),
        this.formlyService.anInput('city', 'GA.GENERAL.CITY')
          .withPlaceholder('GA.GENERAL.CITY')
          .withMaxLength(50)
          .isRequired()
          .withoutWrapper()
          .withWrapperClass('col-8 col-xsm-7 col-sm-8 col-hug-left')
          .build()
      ])
      .build();
  }

  public createAddressValueField(addressModel?:IAddressData):IExtraFieldConfigurationObject {
    return this.formlyService.aValue('location', 'GA.GENERAL.ADDRESS')
      .withFormat((location:ILocationExtended) => {
        return this.formattingService.formatLocationHtml(location, {
          name: addressModel?.vatName,
          number: location?.vatNumber
        });
      })
      .withLabelClass(labelClass)
      .withValueClass(valueClass)
      .withExtraOptions({ templateOptions: { formatWatchers: [ 'model.vatName' ] } })
      .build();
  }

  public createEInvoiceMailAddressField():IExtraFieldConfigurationObject {
    return this.formlyService.anInput('eInvoiceMailAddress', 'GA.GENERAL.EMAIL')
      .isRequired()
      .withInputType('e-mail')
      .withLabelClass(labelClass)
      .withValueClass(valueClass)
      .withInfo(this.translationService.instant('ORDERENTRY.INVOICE.E_INVOICE.INFO'))
      .showInfo()
      .build();

  }

  public createEInvoiceMailAddressValueField():IExtraFieldConfigurationObject {
    return this.formlyService.aValue('eInvoiceMailAddress', 'GA.GENERAL.EMAIL')
      .withLabelClass(labelClass)
      .withValueClass(valueClass)
      .withInfo(this.translationService.instant('ORDERENTRY.INVOICE.E_INVOICE.INFO'))
      .showInfo()
      .build();

  }
}